import {Routes} from '@angular/router';
import { AboutUsComponent } from './components/about-us/about-us.component';
import { AppProfileGuard } from './auth/app-profile.guard';
import { AuthGuard } from './auth/auth.guard';
import { CustomSellerInfoGuard } from './auth/custom-seller-info.guard';
import { ShopAccessGuard } from './auth/shopaccess.guard';
import { BlogEditComponent } from './components/blog-edit/blog-edit.component';
import { BlogListComponent } from './components/blog-list/blog-list.component';
import { BookmarkListComponent } from './components/bookmark-list/bookmark-list.component';
import { CartComponent } from './components/cart/cart.component';
import { ContactComponent } from './components/contact/contact.component';
import { CustomSellerInfoComponent } from './components/custom-seller-info/custom-seller-info.component';
import { CustomerEditComponent } from './components/customer-edit/customer-edit.component';
import { CustomerListComponent } from './components/customer-list/customer-list.component';
import { SkuEditComponent } from './components/sku-edit/sku-edit.component';
import { InvoiceListComponent } from './components/invoice-list/invoice-list.component';
import { InvoiceOrderListComponent } from './components/invoice-order-list/invoice-order-list.component';
import { LegalNotesComponent } from './components/legal-notes/legal-notes.component';
import { MessageDisplayComponent } from './components/message-display/message-display.component';
import { OrderByuerEditComponent } from './components/order-buyer-edit/order-buyer-edit.component';
import { OrderClaimListComponent } from './components/order-claim-list/order-claim-list.component';
import { OrderClaimRequestComponent } from './components/order-claim-request/order-claim-request.component';
import { OrderGroupDetailComponent } from './components/order-group-detail/order-group-detail.component';
import { OrderReceiptPageComponent } from './components/order-receipt/order-receipt-page.component';
import { PurchaseOrderGroupListComponent } from './components/purchase-order-group-list/purchase-order-group-list.component';
import { SaleOrderListComponent } from './components/sale-order-list/sale-order-list.component';
import { EmailQueueComponent } from './components/email-queue/email-queue.component';
import { OrderSellerEditComponent } from './components/order-seller-edit/order-seller-edit.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ScanProviderEditComponent } from './components/scan-provider-edit/scan-provider-edit.component';
import { ScanProviderListComponent } from './components/scan-provider-list/scan-provider-list.component';
import { ScanProviderRunsComponent } from './components/scan-provider-runs/scan-provider-runs.component';
import { ResolutionVocabularyComponent } from './components/resolution-vocabulary/resolution-vocabulary.component';
import { ReferenceTranslationsComponent } from './components/reference-translations/reference-translations.component';
import { CatalogItemDetailComponent } from './components/catalog-item-detail/catalog-item-detail.component';
import { SearchComponent } from './components/search/search.component';
import { SellerInfoComponent } from './components/seller-info/seller-info.component';
import { SettingsComponent } from './components/settings/settings.component';
import { UserListComponent } from './components/shop-user-list/user-list.component';
import { SkuListComponent } from './components/sku-list/sku-list.component';
import { TwoFactorAuthenticationComponent } from './components/tfa/tfa.component';
import { UserEditComponent } from './components/user-edit/user-edit.component';
import { WelcomeAppDataComponent } from './components/welcome-app-data/welcome-app-data.component';
import { carrierGuard } from './auth/carrier.guard';

export const routeConfig: Routes = [
  { path: 'aboutus', component: AboutUsComponent },
  { path: 'legalnotes', component: LegalNotesComponent },
  { path: 'contact', component: ContactComponent },
  { path: 'message', component: MessageDisplayComponent },
  { path: 'search', component: SearchComponent },
  // Public like 'search' above: an anonymous visitor who searches must be able to
  // open a result without being bounced to the login by AuthGuard.
  { path: 'catalog-item/:id', component: CatalogItemDetailComponent },
  {
    // #652: the Merkliste, unlike the two catalog routes above, IS guarded — the list
    // belongs to a profile and its API is [Authorize] (backend #652 decision 3a).
    // No AppProfileGuard: bookmarking has no commercial effect, so it works for a
    // signed-in user whose profile is not (yet) an approved shop customer, matching the
    // BFF, which deliberately does not gate the controller on shop access.
    path: 'bookmark-list',
    component: BookmarkListComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'shop-custom-seller-info/:customerId',
    component: CustomSellerInfoComponent,
    canActivate: [AuthGuard, AppProfileGuard, CustomSellerInfoGuard],
  },
  {
    path: 'customer-edit/:id',
    component: CustomerEditComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.customerEdit:Kunde bearbeiten`, parent: 'shop-customer' } },
  },
  {
    path: 'shop-customer',
    component: CustomerListComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.customerList:Kunden`, parent: 'mein-konto' } },
  },
  {
    path: 'scan-provider-list',
    component: ScanProviderListComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.scanProviderList:Scan Provider`, parent: 'mein-konto' } },
  },
  {
    path: 'scan-provider-edit',
    component: ScanProviderEditComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.scanProviderCreate:Scan Provider erstellen`, parent: 'scan-provider-list' } },
  },
  {
    path: 'scan-provider-edit/:id',
    component: ScanProviderEditComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.scanProviderEdit:Scan Provider bearbeiten`, parent: 'scan-provider-list' } },
  },
  {
    path: 'scan-provider-runs/:id',
    component: ScanProviderRunsComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.scanProviderRuns:Läufe`, parent: 'scan-provider-list' } },
  },
  {
    path: 'resolution-vocabulary',
    component: ResolutionVocabularyComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.resolutionVocabulary:Erkennungsbegriffe`, parent: 'mein-konto' } },
  },
  {
    // #178 slice F / backend #701: carrier-admin curation of the reference-data display names.
    path: 'reference-translations',
    component: ReferenceTranslationsComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.referenceTranslations:Übersetzungen`, parent: 'mein-konto' } },
  },
  {
    // Backend #657 slice 3: carrier-admin view of the e-mail send queue (+ retry of failed mails).
    path: 'email-queue',
    component: EmailQueueComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.emailQueue:E-Mail-Warteschlange`, parent: 'mein-konto' } },
  },
  {
    path: 'sku-list',
    component: SkuListComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.skuList:Artikel`, parent: 'mein-konto' } },
  },
  {
    path: 'sku-edit',
    component: SkuEditComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.skuCreate:Neuer Artikel`, parent: 'sku-list' } },
  },
  {
    path: 'sku-edit/:id',
    component: SkuEditComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.skuEdit:Artikel bearbeiten`, parent: 'sku-list' } },
  },
  {
    path: 'shop-profile',
    component: ProfileComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.profile:Meine Daten`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-welcome-appdata',
    component: WelcomeAppDataComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'shop-user-list',
    component: UserListComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { userListMode: 'users', breadcrumb: { label: $localize`:@@breadcrumb.userList:Benutzer`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-user-edit/:id',
    component: UserEditComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { userListMode: 'users', breadcrumb: { label: $localize`:@@breadcrumb.userEdit:Benutzer bearbeiten`, parent: 'shop-user-list' } },
  },
  {
    path: 'shop-order-buyer-edit/:id',
    component: OrderByuerEditComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.orderBuyerEdit:Einkauf`, parent: 'shop-purchase-order-group-list' } },
  },
  {
    // #205: one order as a receipt-printer slip, opened from the seller's order view.
    path: 'order-receipt/:id',
    component: OrderReceiptPageComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.orderReceipt:Bestellbeleg`, parent: 'shop-sale-order-list' } },
  },
  {
    path: 'shop-sale-order-list',
    component: SaleOrderListComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.saleOrderList:Verkäufe`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-purchase-order-group-list',
    component: PurchaseOrderGroupListComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.purchaseOrderList:Einkäufe`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-order-claim-list',
    component: OrderClaimListComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.orderClaimList:Reklamationen`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-order-seller-edit/:id',
    component: OrderSellerEditComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.orderSellerEdit:Verkauf`, parent: 'shop-sale-order-list' } },
  },
  {
    path: 'shop-settings',
    component: SettingsComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.settings:Einstellungen`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-invoice-list',
    component: InvoiceListComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.invoiceList:Rechnungen`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-invoice-orders',
    component: InvoiceOrderListComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.invoiceOrders:Rechnungsaufträge`, parent: 'shop-invoice-list' } },
  },
  {
    path: 'shop-registration-list',
    component: UserListComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard, carrierGuard],
    data: { userListMode: 'registrations', breadcrumb: { label: $localize`:@@breadcrumb.registrationList:Registrierungen`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-registration-edit/:id',
    component: UserEditComponent,
    canActivate: [AuthGuard, AppProfileGuard, carrierGuard],
    data: { userListMode: 'registrations', breadcrumb: { label: $localize`:@@breadcrumb.registrationEdit:Registrierung bearbeiten`, parent: 'shop-registration-list' } },
  },
  {
    path: 'shop-blog-list',
    component: BlogListComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.blogList:Meldungen`, parent: 'mein-konto' } },
  },
  {
    path: 'shop-blog-edit/:id',
    component: BlogEditComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.blogEdit:Meldung bearbeiten`, parent: 'shop-blog-list' } },
  },
  {
    path: 'shop-blog-edit',
    component: BlogEditComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.blogCreate:Neue Meldung`, parent: 'shop-blog-list' } },
  },
  {
    path: 'shop-order-claim-request',
    component: OrderClaimRequestComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
  },
  {
    path: 'shop-cart',
    component: CartComponent,
    canActivate: [AuthGuard, AppProfileGuard],
  },
  {
    path: 'tfa',
    component: TwoFactorAuthenticationComponent,
    canActivate: [AuthGuard, AppProfileGuard, ShopAccessGuard],
  },
  {
    path: 'seller-info',
    component: SellerInfoComponent,
    canActivate: [AuthGuard, AppProfileGuard],
  },
  {
    path: 'shop-order-group-edit/:id',
    component: OrderGroupDetailComponent,
    canActivate: [AuthGuard, AppProfileGuard],
    data: { breadcrumb: { label: $localize`:@@breadcrumb.orderGroupEdit:Bestellung`, parent: 'shop-purchase-order-group-list' } },
  },
];